import React, { useState } from 'react';
import { SET_CATEGORIES } from '../../constants/blockTypes';
import { colors as theme } from '../../theme';

const colors = {
  bg: theme.bg,
  surface1: theme.surface1,
  surface2: theme.surface2,
  surface3: theme.surface3,
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.12)',
  text: theme.text,
  textSecondary: '#EBEBF5',
  textMuted: theme.textMuted,
  accent: theme.gold,
  accentGlow: 'rgba(212, 175, 55, 0.15)',
};

/**
 * Technique Info Tooltip Component
 *
 * Shows an info icon that reveals a tooltip with client-facing explanation
 * of an exercise technique (set category).
 *
 * Props:
 * - categoryId: Set category ID (for exercise techniques)
 * - title: Optional custom title override
 * - description: Optional custom description override
 */
const TechniqueInfoTooltip = ({ categoryId, title, description }) => {
  const [show, setShow] = useState(false);

  // Determine content based on props
  let tooltipTitle = title;
  let tooltipDescription = description;
  let icon = null;

  if (categoryId && !title) {
    const category = SET_CATEGORIES[categoryId];
    if (!category || categoryId === 'normal') return null;
    tooltipTitle = category.label;
    tooltipDescription = category.clientDescription;
    icon = category.icon;
  }

  if (!tooltipTitle && !tooltipDescription) return null;

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShow(!show);
  };

  const handleClose = () => {
    setShow(false);
  };

  return (
    <span style={styles.wrapper}>
      <button
        type="button"
        onClick={handleToggle}
        style={styles.infoIcon}
        aria-label="Show technique info"
      >
        i
      </button>

      {show && (
        <>
          {/* Backdrop to close on outside click */}
          <div style={styles.backdrop} onClick={handleClose} />

          {/* Tooltip */}
          <div style={styles.tooltip}>
            <div style={styles.tooltipHeader}>
              {icon && <span style={styles.tooltipIcon}>{icon}</span>}
              <strong style={styles.tooltipTitle}>{tooltipTitle}</strong>
              <button
                type="button"
                onClick={handleClose}
                style={styles.closeButton}
                aria-label="Close"
              >
                x
              </button>
            </div>
            <p style={styles.tooltipDescription}>{tooltipDescription}</p>
          </div>
        </>
      )}
    </span>
  );
};

const styles = {
  wrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  infoIcon: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: colors.surface3,
    border: 'none',
    color: colors.textMuted,
    fontSize: '11px',
    fontWeight: 700,
    fontStyle: 'italic',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '6px',
    transition: 'all 0.2s ease',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  tooltip: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    minWidth: '220px',
    maxWidth: '300px',
    padding: '14px 16px',
    backgroundColor: colors.surface1,
    borderRadius: '14px',
    border: `1px solid ${colors.borderLight}`,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  tooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  },
  tooltipIcon: {
    fontSize: '18px',
  },
  tooltipTitle: {
    flex: 1,
    color: colors.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  closeButton: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: colors.surface3,
    border: 'none',
    color: colors.textMuted,
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tooltipDescription: {
    color: colors.textSecondary,
    fontSize: '13px',
    lineHeight: '1.6',
    margin: 0,
  },
};

export default TechniqueInfoTooltip;
